/**
 * Base utilities for Experiments API
 * Shared functions used across all API modules
 */

/**
 * Build base path for Agent Connector experiments API (datasets, evals, execution-groups).
 * Uses pipeline **name** in the URL segment (same as production execution / agent-version APIs),
 * not the pipeline UUID.
 */
export const getBasePath = (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string
): string => {
  // Ensure version is a valid number
  const validVersion = version && version > 0 ? version : 1;
  // Encode path parameters to handle special characters safely
  const path = `/design/realms/${encodeURIComponent(realm)}/pipelines/${encodeURIComponent(pipelineName)}/${validVersion}/agent-connector/${encodeURIComponent(connectorId)}`;
  return path;
};

/**
 * Extract ID from Location header
 */
export const extractIdFromLocation = (
  locationHeader: string | undefined,
  resourcePath: string
): string | null => {
  if (!locationHeader) return null;

  // Extract ID from location header (e.g., /datasets/{id} or full URL)
  const idMatch = locationHeader.match(new RegExp(`/${resourcePath}/([^/?]+)`));
  return idMatch ? idMatch[1] : null;
};

/**
 * Create error with needsRefetch flag
 */
export const createNeedsRefetchError = (
  message: string,
  resourceName?: string
): Error & { needsRefetch: boolean; resourceName?: string } => {
  const error = new Error(message) as Error & {
    needsRefetch: boolean;
    resourceName?: string;
  };
  error.needsRefetch = true;
  if (resourceName) {
    error.resourceName = resourceName;
  }
  return error;
};

export function isNeedsRefetchError(
  error: unknown
): error is Error & { needsRefetch: true; resourceName?: string } {
  return (
    error instanceof Error &&
    'needsRefetch' in error &&
    (error as { needsRefetch?: boolean }).needsRefetch === true
  );
}

/**
 * Normalize timestamp - ensure it's a number (Unix milliseconds)
 * API always returns timestamps as numbers, but we handle edge cases
 */
export const normalizeTimestamp = (timestamp: unknown): number => {
  if (typeof timestamp === 'number') {
    return timestamp;
  }
  if (typeof timestamp === 'string') {
    // If it's already a string (shouldn't happen per API docs, but handle gracefully)
    const parsed = Date.parse(timestamp);
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }
  // Fallback to current time if invalid
  return Date.now();
};

/**
 * Transform array response - handles different API response structures
 */
export const extractArrayFromResponse = <T>(responseData: unknown): T[] => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (
    responseData &&
    typeof responseData === 'object' &&
    'content' in responseData &&
    Array.isArray((responseData as { content: unknown }).content)
  ) {
    return (responseData as { content: T[] }).content;
  }

  if (
    responseData &&
    typeof responseData === 'object' &&
    !Array.isArray(responseData)
  ) {
    // Check if it's a single object with id/name properties
    const obj = responseData as Record<string, unknown>;
    if (obj.id && obj.name) {
      return [responseData as T];
    }

    // Try to find an array property
    const arrayKeys = Object.keys(obj).filter(key => Array.isArray(obj[key]));
    if (arrayKeys.length > 0) {
      return obj[arrayKeys[0]] as T[];
    }
  }

  return [];
};
